'use client'
import { Fragment, forwardRef } from 'react'
import { Field, Switch as HSwitch, Label } from '@headlessui/react'
import { cn } from '../../helpers/classnames'

export interface JosefSwitchProps
  extends Omit<React.ComponentPropsWithoutRef<typeof HSwitch>, 'ref'> {
  label?: { true: string; false: string } | null
  className?: string
  containerClassName?: string
  visualOnly?: boolean
  size?: 'sm' | 'md'
}

export const JosefSwitch = forwardRef<HTMLInputElement, JosefSwitchProps>(
  (
    {
      label = null,
      className,
      containerClassName,
      visualOnly = false,
      size = 'md',
      checked,
      ...rest
    },
    ref
  ) => {
    const trackClass = size === 'sm'
      ? 'h-5 w-9'
      : 'h-6 w-11'
    const dotClass = size === 'sm'
      ? 'size-3'
      : 'size-4'
    const dotChecked = size === 'sm'
      ? 'translate-x-[18px]'
      : 'translate-x-6'
    const dotUnchecked = 'translate-x-1'

    if (visualOnly) {
      return (
        <div className={cn('flex items-center gap-3', containerClassName)}>
          {label ? (
            <span className="text-sm font-medium text-black dark:text-white min-w-0">
              <span className="min-w-32 inline-block">
                {checked ? label.true : label.false}
              </span>
            </span>
          ) : null}
          <div
            className={cn(
              'relative inline-flex items-center rounded-full transition-colors duration-200',
              trackClass,
              checked
                ? 'bg-black dark:bg-white'
                : 'bg-black/20 dark:bg-white/20',
              className
            )}
          >
            <span
              className={cn(
                'rounded-full transition-transform duration-200',
                dotClass,
                checked
                  ? `${dotChecked} bg-white dark:bg-black`
                  : `${dotUnchecked} bg-white dark:bg-gray-300`
              )}
            />
          </div>
        </div>
      )
    }

    return (
      <Field>
        <div className={cn('flex items-center gap-3', containerClassName)}>
          {label && (
            <Label className="text-sm font-medium text-black dark:text-white min-w-0">
              <span className="min-w-32 inline-block">
                {checked ? label.true : label.false}
              </span>
            </Label>
          )}

          <HSwitch as={Fragment} ref={ref} checked={checked} {...rest}>
            {({ checked, disabled }) => {
              return (
                <button
                  className={cn(
                    'relative inline-flex items-center rounded-full transition-colors duration-200',
                    trackClass,
                    checked
                      ? 'bg-black dark:bg-white'
                      : 'bg-black/20 dark:bg-white/20',
                    disabled && 'cursor-not-allowed opacity-50',
                    className
                  )}
                >
                  {label && (
                    <span className="sr-only">
                      {checked ? label.true : label.false}
                    </span>
                  )}
                  <span
                    className={cn(
                      'rounded-full transition-transform duration-200',
                      dotClass,
                      checked
                        ? `${dotChecked} bg-white dark:bg-black`
                        : `${dotUnchecked} bg-white dark:bg-gray-300`
                    )}
                  />
                </button>
              )
            }}
          </HSwitch>
        </div>
      </Field>
    )
  }
)

JosefSwitch.displayName = 'JosefSwitch'
